/**
 * TopologyPage — visuaalne seadmete topoloogia diagramm (SVG node-and-edge).
 *
 * Iga park = üks kaart. Kaardi sees on graafi-stiilis topoloogia:
 *   - Gateway sõlm üleval (centraalne)
 *   - PV mõõdik + GRID mõõdik all-keskel, ühendatud gateway-ga
 *   - PCS sõlm
 *   - BAU sõlm (+ PowerEnv alaml.)
 *   - Inverter sõlmed
 *
 * SVG joonistab kasti (rect) iga seadme jaoks + jooned ühenduse esindamiseks.
 * Värv järgib olekut: roheline=online, punane=offline, hall=puudub.
 *
 * Copyright (c) 2025 Rohepaneel
 */
import { useMemo, useState } from 'react';
import { Header } from '../components/Layout';
import { useParks } from '../contexts/ParksContext';
import { fmt } from '../utils/format';

function statusOf(dev) {
  if (!dev) return 'unknown';
  if (dev._online === false) return 'offline';
  if (dev._online === true) return 'online';
  if (dev._age_ms != null) return dev._age_ms < 60000 ? 'online' : 'offline';
  return 'unknown';
}

const NODE_COL = {
  online:  { fill: '#dcfce7', stroke: '#16a34a', text: '#14532d' },
  offline: { fill: '#fee2e2', stroke: '#dc2626', text: '#7f1d1d' },
  unknown: { fill: '#f1f5f9', stroke: '#94a3b8', text: '#475569' },
};
const EDGE_COL = {
  online:  '#22c55e',
  offline: '#fca5a5',
  unknown: '#cbd5e1',
};

// Üks sõlm — clickable kast SVG-s
function Node({ x, y, w, h, status, label, sublabel, value, icon, onClick }) {
  const c = NODE_COL[status] || NODE_COL.unknown;
  return (
    <g style={{ cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <rect
        x={x} y={y} width={w} height={h} rx={4}
        fill={c.fill} stroke={c.stroke} strokeWidth={1.5}
      />
      {/* Olek-täpp paremas ülemises */}
      <circle cx={x + w - 6} cy={y + 6} r={3} fill={c.stroke} />
      {/* Ikoon vasakul */}
      <text x={x + 6} y={y + 16} fontSize="11">{icon}</text>
      {/* Põhinimi */}
      <text
        x={x + 22} y={y + 15}
        fontSize="9.5" fontWeight="600" fill={c.text}
      >
        {label}
      </text>
      {/* Subnimi (host:port) */}
      {sublabel && (
        <text
          x={x + 6} y={y + 28}
          fontSize="8" fill="#64748b" fontFamily="JetBrains Mono, monospace"
        >
          {sublabel}
        </text>
      )}
      {/* Väärtus all */}
      {value && (
        <text
          x={x + w - 6} y={y + h - 5}
          fontSize="8" fill={c.text} textAnchor="end"
          fontFamily="JetBrains Mono, monospace" fontWeight="600"
        >
          {value}
        </text>
      )}
    </g>
  );
}

// L-shape ühenduse joon (parent → child)
function Edge({ from, to, status }) {
  const col = EDGE_COL[status] || EDGE_COL.unknown;
  // Sirged jooned + nurgad — Manhattan-stiilis
  const mx = (from.x + to.x) / 2;
  const path = `M ${from.x} ${from.y} L ${mx} ${from.y} L ${mx} ${to.y} L ${to.x} ${to.y}`;
  return (
    <path
      d={path}
      stroke={col} strokeWidth={status === 'online' ? 1.8 : 1.2} fill="none"
      strokeDasharray={status === 'online' ? null : '3 3'}
    />
  );
}

// Joonista ühe pargi topoloogia-diagramm
function ParkDiagram({ park }) {
  const meta = park._meta || {};
  const pvStatus = statusOf(park.solar);
  const gridStatus = statusOf(park.grid);
  const pcsStatus = park.pcs_host ? (park.pcs_online ? 'online' : 'offline') : 'unknown';
  const bauStatus = park.bau_host ? (park.bau_online ? 'online' : 'offline') : 'unknown';
  const gwStatus = (pvStatus === 'online' || gridStatus === 'online') ? 'online' : 'offline';

  const W = 340;
  const NODE_W = 96, NODE_H = 36;

  // Layout
  // Row 1 (top):      Gateway (keskel)
  // Row 2:    PV (vasak), GRID (parem)   — child of Gateway
  // Row 3:    PCS                              BAU
  // Row 4:    Inverter 1, 2 ...
  const cx = W / 2;
  const gwY = 8;
  const meterRow = 60;
  const pcsRow = 112;
  const invRow = 164;

  const gw = { x: cx - NODE_W / 2, y: gwY };
  const pvNode = { x: 12, y: meterRow };
  const gridNode = { x: W - NODE_W - 12, y: meterRow };
  const pcsNode = { x: 12, y: pcsRow };
  const bauNode = { x: W - NODE_W - 12, y: pcsRow };

  const inverters = (park.inverters || []).map((inv, i) => ({
    inv,
    status: statusOf(inv),
    pos: { x: 12 + (i % 2) * (W - NODE_W - 24), y: invRow + Math.floor(i / 2) * (NODE_H + 8) },
  }));

  // Diagrammi kõrgus arvutame inverterite arvu järgi
  const invRows = Math.max(0, Math.ceil(inverters.length / 2));
  const H = invRow + invRows * (NODE_H + 8) + 12;

  // Edge connection points (ülemine sõlm → alumine)
  const pBottom = (n) => ({ x: n.x + NODE_W / 2, y: n.y + NODE_H });
  const pTop = (n) => ({ x: n.x + NODE_W / 2, y: n.y });

  return (
    <div style={{
      background: 'white',
      border: '1px solid var(--border)',
      borderRadius: 6,
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    }}>
      {/* Päis */}
      <div style={{
        padding: '8px 12px',
        background: park.online ? '#f0fdf4' : '#fef2f2',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{
          width: 22, height: 22, borderRadius: 4,
          background: park.online ? '#16a34a' : '#dc2626',
          color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 11,
        }}>
          {park.slot}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.82rem' }}>
            {park.name}
          </div>
          <div style={{ fontSize: '0.65rem', color: '#64748b', fontFamily: 'JetBrains Mono, monospace' }}>
            {park.host}
            {meta.external_network && meta.lan_subnet && (
              <span style={{ color: '#94a3b8' }}> ({meta.lan_subnet})</span>
            )}
            {' · '}{meta.cadastral_name || meta.address || ''}
          </div>
        </div>
      </div>

      {/* SVG topoloogia */}
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', maxWidth: '100%' }}>
        {/* Edges (joonised ENNE node'e, et oleksid all) */}
        <Edge from={pBottom(gw)} to={pTop(pvNode)} status={pvStatus === 'online' ? 'online' : 'offline'} />
        <Edge from={pBottom(gw)} to={pTop(gridNode)} status={gridStatus === 'online' ? 'online' : 'offline'} />
        {park.pcs_host && <Edge from={{ x: gw.x, y: gw.y + NODE_H / 2 }} to={{ x: pcsNode.x + NODE_W, y: pcsNode.y + NODE_H / 2 }} status={pcsStatus} />}
        {park.bau_host && <Edge from={{ x: gw.x + NODE_W, y: gw.y + NODE_H / 2 }} to={{ x: bauNode.x, y: bauNode.y + NODE_H / 2 }} status={bauStatus} />}
        {inverters.map((iv, i) => (
          <Edge
            key={`inv-edge-${i}`}
            from={{ x: gw.x + NODE_W / 2, y: gw.y + NODE_H }}
            to={pTop(iv.pos)}
            status={iv.status}
          />
        ))}

        {/* Nodes */}
        <Node {...gw} w={NODE_W} h={NODE_H} status={gwStatus} icon="🌐" label="Gateway"
          sublabel={meta.external_network && meta.lan_subnet
            ? `${park.host} (${meta.lan_subnet.split('/')[0]})`
            : `${park.host}`} />
        <Node {...pvNode} w={NODE_W} h={NODE_H} status={pvStatus} icon="☀" label="PV mõõdik"
          sublabel={`${park.host}:${park.port || 502}`}
          value={park.solar?.p_total != null ? `${fmt(park.solar.p_total, 1)} kW` : '—'} />
        <Node {...gridNode} w={NODE_W} h={NODE_H} status={gridStatus} icon="🔌" label="GRID"
          sublabel={`${park.grid_host || park.host}:${park.grid_port || park.port || 502}`}
          value={park.grid?.p_total != null ? `${fmt(park.grid.p_total, 1)} kW` : '—'} />
        {park.pcs_host && (
          <Node {...pcsNode} w={NODE_W} h={NODE_H} status={pcsStatus} icon="⚡" label="IN-POWER PCS"
            sublabel={meta.external_network && meta.lan_pcs_ip
              ? `:${park.pcs_port || 502} (${meta.lan_pcs_ip})`
              : `${park.pcs_host.split('.').pop()}:${park.pcs_port || 502}`}
            value={park.pcs?.p_total != null ? `${fmt(park.pcs.p_total, 1)} kW` : '—'} />
        )}
        {park.bau_host && (
          <Node {...bauNode} w={NODE_W} h={NODE_H} status={bauStatus} icon="🔋" label="Lithtech BAU"
            sublabel={meta.external_network && meta.lan_bau_ip
              ? `:${park.bau_port || 12230} (${meta.lan_bau_ip})`
              : `${park.bau_host.split('.').pop()}:${park.bau_port || 12230}`}
            value={park.bau?.soc != null ? `SOC ${fmt(park.bau.soc, 0)}%` : '—'} />
        )}
        {inverters.map(({ inv, status, pos }, i) => {
          // Otsi vastav LAN IP _meta.lan_inverter_ips listist
          const lanIp = meta.external_network && Array.isArray(meta.lan_inverter_ips)
            ? meta.lan_inverter_ips[i] : null;
          return (
          <Node
            key={`inv-${i}`}
            {...pos} w={NODE_W} h={NODE_H} status={status}
            icon="📡" label={inv.type}
            sublabel={lanIp
              ? `:${inv.port || 502} (${lanIp})`
              : `${inv.host.split('.').pop()} sl ${inv.slave_id}`}
            value={inv.pac != null ? `${fmt(inv.pac / 1000, 1)} kW` : '—'}
          />
          );
        })}
      </svg>
    </div>
  );
}

export function TopologyPage({ wsConnected }) {
  const { data } = useParks();
  const [filter, setFilter] = useState('');

  const parks = useMemo(() => {
    return (data?.parks || [])
      .filter(p => p.name)
      .slice()
      .sort((a, b) => (a.slot || 0) - (b.slot || 0));
  }, [data]);

  const filterLc = filter.trim().toLowerCase();
  const visibleParks = useMemo(() => {
    if (!filterLc) return parks;
    const m = (s) => (s || '').toString().toLowerCase().includes(filterLc);
    return parks.filter(p =>
      m(p.name) || m(p.host) || m(p._meta?.address) || m(p._meta?.cadastral_name) ||
      m(p.pcs_host) || m(p.bau_host) ||
      (p.inverters || []).some(i => m(i.host) || m(i.type) || m(i.label))
    );
  }, [parks, filterLc]);

  const onlineParks = parks.filter(p => p.online).length;
  const totalSoc = useMemo(() => {
    const socs = parks.map(p => p.bau?.soc).filter(s => s != null);
    return socs.length > 0 ? socs.reduce((a, b) => a + b, 0) / socs.length : null;
  }, [parks]);

  return (
    <div className="h-full flex flex-col" style={{ background: '#f5f7fb' }}>
      <Header title="Seadmete topoloogia" connected={wsConnected} />

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 28px 40px' }}>
        {/* Tööriistariba */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Otsi parki, IP-d, seerianumbrit..."
            style={{
              flex: '1 1 280px', padding: '7px 12px',
              border: '1px solid var(--border-strong)', borderRadius: 4,
              fontFamily: 'inherit', fontSize: '0.82rem', background: 'white',
            }}
          />
          <span style={{ fontSize: '0.74rem', color: '#64748b' }}>
            {visibleParks.length} parki · {onlineParks} online
            {totalSoc != null && ` · keskmine SOC ${fmt(totalSoc, 1)}%`}
          </span>
        </div>

        {/* Legend */}
        <div style={{
          background: 'white', border: '1px solid var(--border)', borderRadius: 4,
          padding: '8px 14px', marginBottom: 16,
          display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap',
          fontSize: '0.72rem', color: '#475569',
        }}>
          <span style={{ fontWeight: 600, color: '#0f172a' }}>Legend:</span>
          <LegendDot color="#22c55e" label="Online ≤ 60s" />
          <LegendDot color="#dc2626" label="Offline" />
          <LegendDot color="#94a3b8" label="Pole konfigureeritud" />
          <span style={{ marginLeft: 'auto' }}>
            <span style={{ display: 'inline-block', width: 14, height: 2, background: '#22c55e', verticalAlign: 'middle', marginRight: 6 }} />
            online ühendus
            <span style={{ display: 'inline-block', width: 14, height: 2, background: '#fca5a5', verticalAlign: 'middle', margin: '0 6px 0 14px', backgroundImage: 'repeating-linear-gradient(90deg, #fca5a5 0 3px, transparent 3px 6px)' }} />
            offline / aegunud
          </span>
        </div>

        {/* Parkide grid — iga park üks SVG-diagramm */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
          gap: 12,
        }}>
          {visibleParks.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', gridColumn: '1 / -1' }}>
              {filter ? `Otsing "${filter}" ei andnud tulemusi.` : 'Ühtegi paigaldatud parki ei leitud.'}
            </div>
          ) : (
            visibleParks.map(park => <ParkDiagram key={park.slot} park={park} />)
          )}
        </div>
      </div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
        background: color,
      }} />
      {label}
    </span>
  );
}

export default TopologyPage;
